import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CollapsibleCard, Row, ContentCard } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { modules } from "byzantine";
import { featureEnabled } from "byzantine/src/Feature";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import { PAYMENT_ACTIVITY_ROUTE } from "../PaymentActivity/routes";
import { ACH_PAYMENT_ROUTE } from "./ach/ACHPaymentRoutes";

// this new entry card is gated by "payment_activity" feature flag in CustomizableTransferMenu.tsx
const PaymentsEntryCard = () => {
  const navigate = useNavigate();
  const { l10n } = useLocalization();
  const features = useUserFeatures();
  const [isOpen, setIsOpen] = useState(true);
  const { showWireTransferOptionInMenu: canNavigateToWiresFlow } =
    modules.wireTemplates.useWirePermissions();
  const shouldShowSingleACHPaymentFlow =
    modules.achPayments.useShouldShowSingleACHPaymentFlow();
  const canSendACHPayment = Boolean(features.ach_payments);
  const canCollectACHPayment = Boolean(features.ach_payments_collect);

  const getMakePaymentDescription = () => {
    if (canSendACHPayment && canCollectACHPayment) {
      return l10n.getString("make-payment-card-description-send-or-collect");
    }

    if (canCollectACHPayment) {
      return l10n.getString("make-payment-card-description-collect");
    }

    return l10n.getString("make-payment-card-description-send");
  };

  const formatCopy = (labels: (string | false | undefined)[], noun: string) => {
    const activeLabels = labels.filter(Boolean);

    if (activeLabels.length === 0) return "";
    if (activeLabels.length === 1) return ` ${activeLabels[0]} ${noun}`;
    if (activeLabels.length === 2)
      return ` ${activeLabels.join(" and ")} ${noun}`;

    return ` ${activeLabels.slice(0, -1).join(", ")}, and ${
      activeLabels[activeLabels.length - 1]
    } ${noun}`;
  };

  const getPaymentCopy = () =>
    formatCopy(
      [
        features.fednow_send && "instant",
        features.bulk_payments && "bulk",
        canNavigateToWiresFlow && "wire",
      ],
      "payments"
    );

  const getActivityCopy = () =>
    formatCopy(
      [
        features.bulk_payments && "bulk",
        shouldShowSingleACHPaymentFlow && "ACH",
        features.fednow_send && "instant",
        canNavigateToWiresFlow && "wire",
      ],
      "activity"
    );

  // mimicking styles from OptionCard.jsx
  const CardTitle = () => (
    <Row alignItems="center" justifyContent="center">
      <Row.Item>
        <h4
          className="padding--top--l fontFamily--body padding--left--l"
          style={{ color: "var(--theme-primary)" }}
        >
          {l10n.getString("payments-label")}
          <i
            className="narmi-icon-bank padding--left--xs"
            style={{
              fontSize: "24px",
              transform: "translateY(10%)",
              display: "inline-block",
            }}
          />
        </h4>
        <div className="padding--bottom--l margin--top--xs padding--left--l">
          {l10n.getString("payments-entry-card-subtitle")}
          {getPaymentCopy()}
        </div>
      </Row.Item>
      <Row.Item shrink>
        <span
          className={`fontColor--theme--primary fontSize--l padding--right--l narmi-icon-chevron-${
            isOpen ? "up" : "down"
          }`}
        ></span>
      </Row.Item>
    </Row>
  );

  const CardOption = ({
    title,
    description,
    handleClickOption,
  }: {
    title: string;
    description: string;
    handleClickOption: () => void;
  }) => (
    <ContentCard kind="button" onClick={handleClickOption}>
      <Row alignItems="center" justifyContent="center">
        <Row.Item>
          <h5 className="fontFamily--body fontColor--theme--primary fontSize--m fontWeight--semibold margin--bottom--xxs">
            {title}
          </h5>
          <p className="fontFamily--body fontSize--s">{description}</p>
        </Row.Item>
        <Row.Item shrink>
          <span className="narmi-icon-arrow-right fontColor--theme--primary fontSize--l"></span>
        </Row.Item>
      </Row>
    </ContentCard>
  );

  const achImportEnabled =
    features.ach_file_import &&
    features.bulk_payments &&
    featureEnabled(features, {
      or: [
        "indigo.ACHPaymentViaImport_permission",
        "indigo.ACHPaymentCollectViaImport_permission",
      ],
    });

  const wireImportEnabled =
    features.domestic_wire_file_import &&
    featureEnabled(features, {
      or: ["indigo.DomesticWireImport_permission"],
    });
  const fileImportEnabled = achImportEnabled || wireImportEnabled;

  const getFileImportDescription = () => {
    if (achImportEnabled && wireImportEnabled) {
      return l10n.getString("file-import-both-description");
    }
    if (wireImportEnabled) {
      return l10n.getString("wire-file-import-description");
    }
    return l10n.getString("make-bulk-payment-from-file-description");
  };

  const cardOptionsCopy = [
    // this option will always exist, but we show different copy depending on what flags are available
    {
      title: l10n.getString("view-activity-label"),
      description: `${l10n.getString(
        "view-activity-card-description"
      )} ${getActivityCopy()}`,
      handleClickOption: () => {
        navigate(`/${PAYMENT_ACTIVITY_ROUTE}`);
      },
    },
    // check for bulk_payments flag
    features.bulk_payments && {
      title: l10n.getString("make-a-bulk-payment-label"),
      description: l10n.getString("make-bulk-payment-card-description"),
      handleClickOption: () => {
        navigate(`/${PAYMENT_ACTIVITY_ROUTE}/bulk/new`);
      },
    },
    shouldShowSingleACHPaymentFlow && {
      title: l10n.getString("button-make-a-payment"),
      description: getMakePaymentDescription(),
      handleClickOption: () => {
        navigate(`/transfer/${ACH_PAYMENT_ROUTE}`);
      },
    },
    // check for ach_file_import or domestic_wire_file_import with appropriate permissions
    fileImportEnabled && {
      title: l10n.getString("make-bulk-payment-from-file-label"),
      description: getFileImportDescription(),
      handleClickOption: () => {
        navigate(`/${PAYMENT_ACTIVITY_ROUTE}/upload`);
      },
    },
    canNavigateToWiresFlow && {
      title: l10n.getString("send-a-wire-card-title"),
      description: l10n.getString("send-wire-card-description"),
      handleClickOption: () => {
        navigate("/transfer/wires");
      },
    },
  ].filter(Boolean) as {
    title: string;
    description: string;
    handleClickOption: () => void;
  }[];

  return (
    <div className="transfer-option-card full-width-option-card">
      <CollapsibleCard
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        renderTitle={() => <CardTitle />}
      >
        {cardOptionsCopy.map((option, index) => (
          <div
            key={`payments-entry-card-option-${index}`}
            className={
              index < cardOptionsCopy.length - 1 ? "margin--bottom--s" : ""
            }
          >
            <CardOption
              title={option.title}
              description={option.description}
              handleClickOption={option.handleClickOption}
            />
          </div>
        ))}
      </CollapsibleCard>
    </div>
  );
};

export default PaymentsEntryCard;
